import React, {useState} from 'react'
import * as Yup from 'yup'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {Field, Form, Formik, ErrorMessage} from 'formik'
import {postRequest} from '../../../../setup/redux/common/cruds'
import Swal from 'sweetalert2'
import {Loader} from '../../../services/Loader'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {AuthFooter} from '../../../services/Functional'
import {CustomInput} from './custominput/CustomInput'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import PasswordValidationUI, {
  checkPasswordValidity,
} from '../../../components/common/PasswordValidationUI'

const initialValues = {
  token: '',
  email: '',
  password: '',
  confirm_password: '',
}

const forgotPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Password does not match')
    .required('Confirm password is required'),
})

export function ResetPassword() {
  const params: any = useParams()
  let {tokenDetail} = params
  tokenDetail = atob(tokenDetail)
  const data = JSON.parse(tokenDetail)
  const {email, token} = data
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [isTouched, setIsTouched] = useState<boolean>(false)
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false)
  const [passwordShow, setPasswordShow] = useState<boolean>(false)
  const [confirmPasswordShow, setConfirmPasswordShow] = useState<boolean>(false)
  const {config}: any = useSelector<RootState>(
    (state) => ({
      config: state.configuration.configurations,
    }),
    shallowEqual
  )

  React.useEffect(() => {
    setLoading(true)
    postRequest(
      `${process.env.REACT_APP_API_BASE_URL}auth/validate-reset-password-token`,
      data
    ).then((response) => {
      setLoading(false)
      if (!response.data.success) {
        Swal.fire({
          title: '',
          html: response.data.message,
          icon: 'warning',
          allowOutsideClick: false,
          heightAuto: false,
        }).then(() => {
          Swal.close()
          navigate('/auth/login')
        })
      }
    })
  }, [])

  const [hasErrors, setHasErrors] = useState(undefined)

  const passwordUpdateRequest = (values: any) => {
    setLoading(true)
    setHasErrors(undefined)
    let payload: any = {
      email,
      token,
      password: values.password,
      confirm_password: values.confirm_password,
    }
    postRequest(`${process.env.REACT_APP_API_BASE_URL}auth/reset-password`, payload).then(
      (response) => {
        setLoading(false)
        Swal.fire({
          title: response.data.success ? 'Success' : 'Warning',
          html: response.data.message,
          icon: response.data.success ? 'success' : 'warning',
          allowOutsideClick: false,
          heightAuto: false,
        }).then(() => {
          navigate('/auth/login')
          Swal.close()
        }).catch((e: any) => {
          setLoading(false)
        })
      }
    )
  }

  return (
    <>
      {loading && <Loader />}
      <div className='row w-100 h-100 p-0 m-0'>
        <div className='col-md-6 background_card'>
          <img src={config && config?.actual_logo} alt='' />
        </div>
        <div className='col-md-6 p-0 bg-white position-relative'>
          <div className='mx-auto d-flex justify-content-center align-items-center h-100'>
            <Formik
              enableReinitialize={true}
              initialValues={initialValues}
              validationSchema={forgotPasswordSchema}
              onSubmit={(values) => {
                passwordUpdateRequest(values)
              }}
            >
              {({values, handleSubmit, handleChange, errors, touched, handleBlur}) => {
                return (
                  <Form
                    className='fv-plugins-bootstrap5 fv-plugins-framework auth_form form_align'
                    noValidate
                    id='kt_login_password_reset_form'
                  >
                    <div className='text-center mb-6 animate__animated animate__fadeInUp'>
                      <h1 className='text-dark mb-3'>Set New Password</h1>
                      <div className='fs-4'>Set a strong, secure password for your account.</div>
                    </div>

                    {hasErrors === true && (
                      <div className='mb-6 alert alert-danger'>
                        <div className='alert-text font-weight-bold'>
                          Sorry, looks like there are some errors detected, please try again.
                        </div>
                      </div>
                    )}

                    {hasErrors === false && (
                      <div className='mb-6 bg-light-info p-8 rounded'>
                        <div className='text-info'>Password set successfully.</div>
                      </div>
                    )}

                    <div className={`fv-row animate__animated animate__fadeInUp ${isTouched ? 'mb-0' : 'mb-3'}`}>
                      <label className='font-size-h6 mb-1.5'>
                        Password<span className='text-danger'>*</span>
                      </label>
                      <Field
                        name='password'
                        type={!passwordShow ? 'password' : 'text'}
                        component={CustomInput}
                        placeholder='******'
                        className='form-control password-field'
                        onFocus={() => setIsTouched(true)}
                        onChange={(e: any) => {
                          handleChange(e)
                          const newPassword = e.target.value
                          setIsPasswordValid(checkPasswordValidity(newPassword))
                        }}
                        icon={
                          <img
                            className='cursor-pointer'
                            alt='show'
                            src={
                              !passwordShow
                                ? toAbsoluteUrl('/media/Hide.svg')
                                : toAbsoluteUrl('/media/Show.svg')
                            }
                            onClick={() => setPasswordShow(!passwordShow)}
                          />
                        }
                      />
                      <ErrorMessage name='password' component='div' className='text-danger mt-2' />
                      {isTouched && (
                        <div className='mt-7' style={{ maxWidth: '350px' }}>
                          <PasswordValidationUI password={values.password} isTouched={isTouched} />
                        </div>
                      )}
                    </div>

                    <div className='fv-row mb-6 animate__animated animate__fadeInUp'>
                      <label className='font-size-h6 mb-1.5'>
                        Confirm Password<span className='text-danger'>*</span>
                      </label>
                      <Field
                        name='confirm_password'
                        type={!confirmPasswordShow ? 'password' : 'text'}
                        component={CustomInput}
                        placeholder='******'
                        className='form-control'
                        icon={
                          <img
                            className='cursor-pointer'
                            alt='show'
                            src={
                              !confirmPasswordShow
                                ? toAbsoluteUrl('/media/Hide.svg')
                                : toAbsoluteUrl('/media/Show.svg')
                            }
                            onClick={() => setConfirmPasswordShow(!confirmPasswordShow)}
                          />
                        }
                      />
                      <ErrorMessage
                        name='confirm_password'
                        component='div'
                        className='text-danger mt-2'
                      />
                    </div>

                    <div className='d-flex justify-content-between w-100 pb-lg-0 animate__animated animate__fadeInUp'>
                      <Link to='/auth/login' className='w-50 me-3'>
                        <button
                          type='button'
                          id='kt_login_password_reset_form_cancel_button'
                          className='btn btn-lg btn-light-primary rounded-pill fw-bolder w-100 py-3'
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </Link>{' '}
                      <button
                        type='submit'
                        id='kt_password_reset_submit'
                        className={`btn btn-lg btn-primary rounded-pill fw-bolder w-50 py-3 ${!isPasswordValid ? 'disabled-btn' : ''}`}
                        disabled={!isPasswordValid}
                        onSubmit={() => handleSubmit}
                      >
                        <span className='indicator-label'>Set New</span>
                        {loading && (
                          <span className='indicator-progress'>
                            Please wait...
                            <span className='spinner-border spinner-border-sm align-middle ms-2' />
                          </span>
                        )}
                      </button>
                    </div>
                    <div className='d-flex align-items-center justify-content-center'>
                      <AuthFooter />
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  )
}
